// components/legal.jsx — Privacy Policy and Terms & Conditions.
//
// Client-supplied copy (Content/Enginuity_Privacy_Policy_and_Terms_Conditions_Completed.docx),
// transcribed verbatim. Drafted to align with POPIA and South African
// e-commerce/consumer law principles for engn.co.za's actual data
// touchpoints (contact form, careers/CV submissions, Insights subscription,
// analytics/tracking scripts).

'use client';

import { PageHero, Section, Headline } from './ui';

const LAST_UPDATED = '14 September 2026';

function LegalSection({ title, children }) {
  return (
    <div style={{ paddingTop: 36, marginTop: 36, borderTop: '1px solid var(--hair)' }}>
      <Headline size="sm" as="h2" style={{ marginBottom: 14 }}>{title}</Headline>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 15.5, lineHeight: 1.7, color: 'var(--fg2)' }}>
        {children}
      </div>
    </div>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <PageHero eyebrow="Legal" title="Privacy Policy" sub={`Last updated: ${LAST_UPDATED}`} tone="industrial" />
      <Section>
        <div style={{ maxWidth: '72ch' }}>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: 'var(--fg)' }}>
            Enginuity SA (Pty) Ltd ("Enginuity", "we", "us", or "our") is committed to protecting the privacy
            and personal information of everyone who visits or interacts with our website at engn.co.za (the
            "Site"). This Privacy Policy explains what personal information we collect, why we collect it, how
            we use and protect it, and what rights you have, in accordance with the Protection of Personal
            Information Act 4 of 2013 ("POPIA") and any other applicable data protection legislation.
          </p>
          <p style={{ fontSize: 15.5, lineHeight: 1.7, color: 'var(--fg2)' }}>
            By using the Site, submitting a form, applying for a position with us, or otherwise providing us
            with your personal information, you consent to the collection and processing of that information
            as described in this Policy.
          </p>

          <LegalSection title="1. Who we are">
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>Company name: Enginuity SA (Pty) Ltd</li>
              <li>Registration number: 2023/699476/07</li>
              <li>Registered address: Unit 27 Minifactory, Limeroc Business Park, Knoppieslaagte, Centurion, 0157</li>
              <li>Website: www.engn.co.za</li>
              <li>General enquiries: karla.naude@engn.co.za</li>
              <li>Information Officer: Karla Naude, +27 82 717 4774</li>
            </ul>
          </LegalSection>

          <LegalSection title="2. Personal information we collect">
            <p>
              We collect personal information directly from you when you interact with the Site, including
              through the following touchpoints:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>Contact and enquiry forms: name, surname, email address, phone number, company name, and the content of your message.</li>
              <li>Careers and CV submissions: name, surname, contact details, CV/resume content (which may include employment history, qualifications, and references), and any covering information you choose to submit to careers@engn.co.za.</li>
              <li>Insights / blog subscription: email address, and any preferences you select, if you subscribe to receive updates from our Insights section.</li>
              <li>Website usage data: IP address, browser and device type, pages visited, referring website, and interaction data, collected automatically through cookies and analytics or tracking scripts (see Section 4 below).</li>
            </ul>
            <p>
              We do not knowingly collect special personal information (such as health, religious, or biometric
              information) through the Site. Where a CV or application voluntarily includes such information,
              it will only be used for recruitment purposes and handled with appropriate additional care.
            </p>
          </LegalSection>

          <LegalSection title="3. Why we collect and how we use your information">
            <p>
              We process personal information for the following purposes, and on the following lawful grounds
              under POPIA:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>To respond to enquiries and correspond with you: processed on the basis of your consent and/or to take steps prior to entering into a contract.</li>
              <li>To assess job applications and manage recruitment: processed on the basis of your consent and our legitimate interest in recruiting suitable candidates.</li>
              <li>To send Insights/blog updates you have subscribed to: processed on the basis of your consent, which you may withdraw at any time.</li>
              <li>To understand how visitors use the Site, improve its content and performance, and measure the effectiveness of our search and AI visibility optimisation: processed on the basis of consent (for non-essential cookies) and our legitimate interest in operating and improving the Site.</li>
              <li>To comply with legal, regulatory, or contractual obligations.</li>
            </ul>
          </LegalSection>

          <LegalSection title="4. Cookies and tracking technologies">
            <p>
              The Site uses cookies and similar tracking technologies, including analytics and tracking
              scripts, to understand website performance and visitor behaviour. These may include:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>Strictly necessary cookies, required for the Site to function correctly.</li>
              <li>Analytics cookies and tracking scripts, used to measure traffic, engagement, and search/AI visibility performance.</li>
              <li>Functional cookies, used to remember preferences and improve your experience.</li>
            </ul>
            <p>
              Where required by law, we will request your consent before placing non-essential cookies or
              tracking scripts on your device, and you will be able to manage or withdraw your preferences
              through the cookie banner or settings on the Site.
            </p>
          </LegalSection>

          <LegalSection title="5. Sharing of personal information">
            <p>We do not sell personal information. We may share personal information with:</p>
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>Service providers who support the operation of the Site, such as hosting providers, analytics providers, and our marketing/SEO agency, who process information on our behalf and under instruction.</li>
              <li>Enginuity's affiliated office in St Gallen, Switzerland, where relevant to the services or enquiry (see Section 6, Cross-Border Transfers).</li>
              <li>Professional advisors, regulators, or authorities where required by law.</li>
            </ul>
            <p>
              We require all third parties who process personal information on our behalf to keep it secure
              and to use it only for the purposes we specify.
            </p>
          </LegalSection>

          <LegalSection title="6. Cross-border transfers">
            <p>
              As Enginuity operates in both South Africa and Switzerland, personal information collected via
              the Site may, where relevant to your enquiry or application, be transferred to and processed in
              Switzerland. Switzerland is recognised as providing an adequate level of data protection. Where
              personal information is transferred to any other country, we will ensure appropriate safeguards
              are in place, consistent with the requirements of POPIA.
            </p>
          </LegalSection>

          <LegalSection title="7. Data retention">
            <p>
              We retain personal information only for as long as necessary to fulfil the purpose for which it
              was collected, or as required by law. Job application data that is unsuccessful is generally
              retained for a limited period for future recruitment purposes, after which it is securely
              deleted, unless you have asked us to remove it sooner.
            </p>
          </LegalSection>

          <LegalSection title="8. Your rights under POPIA">
            <p>Subject to POPIA, you have the right to:</p>
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>Be informed of what personal information we hold about you and how it is used.</li>
              <li>Request access to the personal information we hold about you.</li>
              <li>Request correction or updating of inaccurate, outdated, or incomplete information.</li>
              <li>Request deletion or destruction of personal information we no longer have a lawful basis to hold.</li>
              <li>Object to the processing of your personal information, including for direct marketing purposes.</li>
              <li>Withdraw consent at any time, where processing is based on consent, without affecting the lawfulness of processing before withdrawal.</li>
              <li>Lodge a complaint with the Information Regulator of South Africa if you believe your personal information has been processed unlawfully.</li>
            </ul>
            <p>To exercise any of these rights, please contact us using the details in Section 11 below.</p>
          </LegalSection>

          <LegalSection title="9. Security measures">
            <p>
              We implement reasonable technical and organisational measures to protect personal information
              against loss, unauthorised access, alteration, or disclosure, appropriate to the nature of the
              information and the risks involved. However, no method of transmission or storage is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </LegalSection>

          <LegalSection title="10. Children's information">
            <p>
              The Site is not directed at children, and we do not knowingly collect personal information from
              children without the consent of a parent or guardian, as required by POPIA.
            </p>
          </LegalSection>

          <LegalSection title="11. Contact us and the Information Regulator">
            <p>
              If you have questions about this Privacy Policy, or wish to exercise any of your rights, please
              contact:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22, listStyle: 'none' }}>
              <li>Email: <a href="mailto:karla.naude@engn.co.za" style={{ color: 'var(--fg)', fontWeight: 600 }}>karla.naude@engn.co.za</a></li>
              <li>Information Officer: Karla Naude, +27 82 717 4774</li>
            </ul>
            <p>
              If you are not satisfied with our response, you may lodge a complaint with the Information
              Regulator of South Africa:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22, listStyle: 'none' }}>
              <li>Website: <a href="https://www.inforegulator.org.za" target="_blank" rel="noreferrer" style={{ color: 'var(--fg)' }}>www.inforegulator.org.za</a></li>
              <li>Email: <a href="mailto:complaints.IR@justice.gov.za" style={{ color: 'var(--fg)' }}>complaints.IR@justice.gov.za</a></li>
            </ul>
          </LegalSection>

          <LegalSection title="12. Changes to this Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices,
              technology, or legal requirements. The date of the most recent update will be reflected at the
              top of this Policy. We encourage you to review this Policy periodically.
            </p>
          </LegalSection>
        </div>
      </Section>
    </>
  );
}

function TermsAndConditions() {
  return (
    <>
      <PageHero eyebrow="Legal" title="Terms &amp; Conditions" sub={`Last updated: ${LAST_UPDATED}`} tone="industrial" />
      <Section>
        <div style={{ maxWidth: '72ch' }}>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: 'var(--fg)' }}>
            These Terms and Conditions ("Terms") govern your access to and use of the website www.engn.co.za
            (the "Site"), operated by Enginuity SA (Pty) Ltd ("Enginuity", "we", "us", or "our"). By accessing
            or using the Site, you agree to be bound by these Terms. If you do not agree with these Terms,
            please do not use the Site.
          </p>

          <LegalSection title="1. About Enginuity">
            <p>
              Enginuity is a multi-disciplinary engineering, EPCM (engineering, procurement, and construction
              management), and technology-development partner to the mining, minerals processing, and
              industrial sectors, with operations in Centurion, South Africa and St Gallen, Switzerland.
            </p>
          </LegalSection>

          <LegalSection title="2. Use of the Site">
            <p>
              You agree to use the Site only for lawful purposes and in a manner that does not infringe the
              rights of, or restrict or inhibit the use and enjoyment of the Site by, any third party. You may
              not:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22 }}>
              <li>Use the Site in any way that breaches any applicable local, national, or international law or regulation.</li>
              <li>Attempt to gain unauthorised access to the Site, the server on which it is hosted, or any server, computer, or database connected to it.</li>
              <li>Introduce any viruses, malware, or other material that is malicious or technologically harmful.</li>
              <li>Use any automated system, including "robots" or "spiders", to access the Site without our prior written consent.</li>
            </ul>
          </LegalSection>

          <LegalSection title="3. Intellectual property">
            <p>
              All content on the Site, including text, graphics, logos, images, the Enginuity name and brand,
              and the compilation and arrangement of such content, is the property of Enginuity or its
              licensors and is protected by South African and international intellectual property laws. You
              may view, download, and print content from the Site for your own personal, non-commercial use,
              provided you do not modify the content and you retain all copyright and proprietary notices.
            </p>
          </LegalSection>

          <LegalSection title="4. Accuracy of information">
            <p>
              We take reasonable care to ensure that the information on the Site, including descriptions of
              our services, capabilities, and projects, is accurate and up to date. However, the content of
              the Site is provided for general information purposes only and does not constitute professional,
              technical, or engineering advice. No content on the Site should be relied upon as a substitute
              for a formal proposal, scope of work, or engineering engagement with Enginuity. We reserve the
              right to amend, update, or remove content on the Site at any time without notice.
            </p>
          </LegalSection>

          <LegalSection title="5. Enquiries, quotes, and engagements">
            <p>
              Submitting an enquiry, contact form, or request through the Site does not create a binding
              contract between you and Enginuity. Any engagement for engineering, EPCM, or related services
              will only be formalised through a separate, signed agreement or proposal between Enginuity and
              the relevant client.
            </p>
          </LegalSection>

          <LegalSection title="6. Careers and job applications">
            <p>
              Where you submit a CV or job application through the Site, you confirm that the information
              provided is true, accurate, and complete to the best of your knowledge. Submission of an
              application does not guarantee an interview or offer of employment. Application and CV
              information will be handled in accordance with our Privacy Policy.
            </p>
          </LegalSection>

          <LegalSection title="7. Insights and subscriptions">
            <p>
              Where you subscribe to receive updates from our Insights section, you consent to receive such
              communications by email. You may unsubscribe at any time using the link provided in any
              communication we send, or by contacting us directly.
            </p>
          </LegalSection>

          <LegalSection title="8. Third-party links">
            <p>
              The Site may contain links to third-party websites. These links are provided for your
              convenience only. We do not endorse and are not responsible for the content, accuracy, or
              practices of any third-party website, and accessing such websites is at your own risk.
            </p>
          </LegalSection>

          <LegalSection title="9. Limitation of liability">
            <p>
              To the fullest extent permitted by law, Enginuity, its directors, employees, and agents will not
              be liable for any direct, indirect, incidental, special, or consequential loss or damage arising
              out of, or in connection with, your use of, or inability to use, the Site, including but not
              limited to loss of data, revenue, or business opportunity. Nothing in these Terms limits or
              excludes liability that cannot lawfully be limited or excluded under South African law.
            </p>
          </LegalSection>

          <LegalSection title="10. Indemnity">
            <p>
              You agree to indemnify and hold Enginuity harmless from and against any claims, losses, damages,
              liabilities, and expenses (including reasonable legal costs) arising out of your breach of these
              Terms or your misuse of the Site.
            </p>
          </LegalSection>

          <LegalSection title="11. Governing law and jurisdiction">
            <p>
              These Terms are governed by the laws of the Republic of South Africa. Any dispute arising from
              these Terms or your use of the Site will be subject to the exclusive jurisdiction of the South
              African courts.
            </p>
          </LegalSection>

          <LegalSection title="12. Changes to these Terms">
            <p>
              We may update these Terms from time to time. Any changes will be posted on this page with an
              updated revision date. Your continued use of the Site after any changes constitutes your
              acceptance of the revised Terms.
            </p>
          </LegalSection>

          <LegalSection title="13. Contact us">
            <ul style={{ margin: 0, paddingLeft: 22, listStyle: 'none' }}>
              <li>Company: Enginuity SA (Pty) Ltd</li>
              <li>Registration number: 2023/699476/07</li>
              <li>Registered address: Unit 27 Minifactory, Limeroc Business Park, Knoppieslaagte, Centurion, 0157</li>
              <li>Email: <a href="mailto:karla.naude@engn.co.za" style={{ color: 'var(--fg)', fontWeight: 600 }}>karla.naude@engn.co.za</a></li>
              <li>Website: www.engn.co.za</li>
            </ul>
          </LegalSection>
        </div>
      </Section>
    </>
  );
}

export { PrivacyPolicy, TermsAndConditions };
